import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DATA_ADAPTER } from '../config/adapters';
import {
  isCryptoSnapshotFresh,
  type CryptoRatesSnapshot,
} from '../core/exchange/cryptoRates';
import { useRoleCollection } from './useRoleCollection';

interface CryptoRefreshResult {
  ok: boolean;
  skipped?: boolean;
  refreshed?: boolean;
  keptPrevious?: boolean;
}

function isOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}

export function useCryptoRates() {
  const rows = useRoleCollection<CryptoRatesSnapshot[]>('cryptoRates', []);
  const [refreshing, setRefreshing] = useState(false);
  const [failed, setFailed] = useState(false);
  const snapshotRef = useRef<CryptoRatesSnapshot | null>(null);
  const refreshingRef = useRef(false);

  const snapshot = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const row = rows[0];
    return row?.quotes?.length ? row : null;
  }, [rows]);

  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  useEffect(() => {
    refreshingRef.current = refreshing;
  }, [refreshing]);

  const refresh = useCallback(async (force = false) => {
    if (DATA_ADAPTER !== 'firebase' || refreshingRef.current) return false;
    if (!isOnline()) {
      setFailed(true);
      return false;
    }
    if (!force && isCryptoSnapshotFresh(snapshotRef.current)) return false;

    setRefreshing(true);
    setFailed(false);
    try {
      const { refreshCryptoRates } = await import('../services/firebase/FunctionsService');
      const result = await refreshCryptoRates(force) as CryptoRefreshResult;
      if (!result?.ok) {
        setFailed(true);
        return false;
      }
      return Boolean(result.refreshed);
    } catch {
      setFailed(true);
      return false;
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (DATA_ADAPTER !== 'firebase') return undefined;

    const tryAuto = () => {
      if (!isOnline()) return;
      if (isCryptoSnapshotFresh(snapshotRef.current)) return;
      void refresh(false);
    };

    tryAuto();
    window.addEventListener('online', tryAuto);
    return () => window.removeEventListener('online', tryAuto);
  }, [refresh]);

  return {
    snapshot,
    refreshing,
    failed,
    refresh,
    /** True when Firebase mode may still hydrate / pull crypto quotes. */
    enabled: DATA_ADAPTER === 'firebase',
  };
}
